/**
 * Result-grid builders behind the TR, RHISTORY and RTGET custom functions
 * (and their RDP.* aliases). Kept free of Office globals so vitest can
 * import them under node; functions/functions.ts only wires them to Excel.
 *
 * Failures come back as LSEG's plain-text cell values (spec §5.2), never as
 * Excel errors, so `ISTEXT` sees them the way it sees LSEG's. The one
 * exception is CellError, thrown for the few cases that have no LSEG text
 * yet; functions.ts turns it into a CustomFunctions.Error.
 */

import { evaluate, EvaluateResponse, TableResult } from "./api";
import { fetchRicsSettled, isTransient, SettledRics } from "./batcher";
import { shareInFlight, sharedReask } from "./reask";
import {
  errorToCellText,
  invalidRics,
  isLsegError,
  isUnresolvedError,
  isUnsupportedFieldsError,
  LSEG_TEXT,
  rowFieldError,
} from "./lsegText";
import { Cell, Table } from "./layout";
import {
  DateSerial,
  TimeSerial,
  NaError,
  defaultValueColumn,
  dropInstrumentColumn,
  flattenFields,
  flattenInstruments,
  hasLegacyHistoryFields,
  historyRefreshError,
  historyRequestError,
  intradayMinutes,
  isIntradayInterval,
  isTickInterval,
  isBarField,
  layoutHistoryCells,
  layoutNoRows,
  layoutTrCells,
  looksLikeParams,
  mapHistoryField,
  mergeHistory,
  mergeRealtime,
  normalizeDate,
  normalizeHistoryRequest,
  parseHistoryDisplay,
  parseTrParams,
  planTr,
  ricKey,
  scalarText,
  splitByInstrument,
  splitHistory,
  trimHistory,
  typedField,
  trOutputHeaders,
  unmarkDates,
  isDataGridOutput,
  isInvalidIdentifier,
  TrDisplay,
  TrOutput,
  TrPlan,
  widenHistoryWindow,
  HistoryColumn,
} from "./refinitiv";

// Max RICs merged into one backend request (see lib/batcher.ts).
const TR_BATCH = 100;
const RT_BATCH = 50; // RtGet subscribes each RIC on the live stream
const HISTORY_BATCH = 50;

/** A failure with no LSEG text: functions.ts shows it as this Excel error. */
export class CellError extends Error {
  code: "invalidValue" | "notAvailable";
  constructor(code: "invalidValue" | "notAvailable", message: string) {
    super(message);
    this.name = "CellError";
    this.code = code;
  }
}

// --- Formula assembly ------------------------------------------------------

/** Quote a string argument, escaping embedded quotes Excel-style ("" ). */
export function q(v: string | null | undefined): string | null {
  if (v === null || v === undefined || v === "") return null;
  return `"${String(v).replace(/"/g, '""')}"`;
}

/** Format a numeric argument; blank/omitted → null (dropped from the call). */
export function n(v: number | null | undefined): string | null {
  if (v === null || v === undefined || (v as any) === "") return null;
  if (typeof v === "number" && !isFinite(v)) return null;
  return String(v);
}

/**
 * Build `=NAME(a, b, c)` from positional tokens. Trailing empty tokens are
 * dropped; interior empties become bare `,` placeholders so later positional
 * args still line up (the interface parser treats an empty arg as omitted),
 * mirroring `=RHistory("AAPL.O", "TRDPRC_1", "1H", , , 50)`.
 */
export function buildCall(name: string, tokens: (string | null)[]): string {
  let last = -1;
  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i] !== null && tokens[i] !== "") last = i;
  }
  const used = tokens.slice(0, last + 1).map((t) => (t === null ? "" : t));
  return `=${name}(${used.join(", ")})`;
}

// --- Result shaping --------------------------------------------------------

/** Excel custom functions reject null/undefined cells — coerce to "". */
function cell(v: any): any {
  return v === null || v === undefined ? "" : v;
}

/**
 * Turn an EvaluateResponse into the 2-D array Excel spills. A leading header
 * row of column names is included (matching the Workspace add-in default).
 * History-style frames carry an `index` column, prepended as "index".
 */
export function toMatrix(resp: EvaluateResponse): any[][] {
  const { columns, data, index } = resp.result;
  const hasIndex = Array.isArray(index) && index.length === data.length;
  const header = (hasIndex ? ["index"] : []).concat(columns || []);
  const rows = (data || []).map((r, i) => (hasIndex ? [index![i], ...r] : r));

  if (rows.length === 0) {
    // Still surface column names so the user sees the shape; otherwise a
    // friendly placeholder instead of a bare #VALUE!.
    return header.length ? [header.map(cell)] : [["(no data)"]];
  }
  return [header.map(cell), ...rows.map((r) => r.map(cell))];
}

const text = (s: string): any[][] => [[s]];

/**
 * A grid as the static writer's Table: "" and null become truly empty cells,
 * DateSerial cells date cells, TimeSerial cells time cells. `cornerEmpty`: the grid has both a row-header
 * and a column-header axis, so its top-left cell is the empty corner.
 */
export function toTable(grid: any[][], cornerEmpty = false): Table {
  const cells: Cell[][] = grid.map((row) =>
    row.map((v): Cell => {
      if (v instanceof DateSerial) return v.datetime ? { v: v.serial, date: true, datetime: true } : { v: v.serial, date: true };
      if (v instanceof TimeSerial) return { v: v.serial, time: true };
      if (v instanceof NaError) return { v: "#N/A", error: true };
      return { v: v === "" || v === null || v === undefined ? null : v };
    })
  );
  return { cells, cornerEmpty: cornerEmpty && cells.length > 0 };
}

/** A laid-out grid (date cells still marked) and whether its top-left is the empty corner. */
interface Laid {
  grid: any[][];
  cornerEmpty: boolean;
}
const plain = (grid: any[][]): Laid => ({ grid, cornerEmpty: false });

// --- Live updates (spec §3a) -----------------------------------------------------

/**
 * A result whose realtime cells tick: the stream subscription it needs and
 * how a tick rebuilds its table. TR.* fields and RHistory have none (one-shot).
 */
export interface LiveFeed {
  /** RICs to subscribe, as typed (distinct, non-blank). */
  rics: string[];
  /** FIDs to subscribe them with. */
  fields: string[];
  /** Merge a tick of `ric`; the rebuilt table, or null when no cell changed. */
  tick(ric: string, fields: Record<string, any>): Table | null;
}

/** A built result: the table to lay out now, and its live feed if its cells tick. */
export interface Built {
  table: Table;
  live: LiveFeed | null;
}

const isBlank = (v: any) => v === null || v === undefined || v === "";

/** A tick's value for `fid` (FIDs compared case-insensitively); undefined when absent or blank. */
function tickValue(fields: Record<string, any>, fid: string): any {
  const k = Object.keys(fields).find((x) => x.toUpperCase() === fid.toUpperCase());
  return k === undefined || isBlank(fields[k]) ? undefined : fields[k];
}

const sameTable = (a: Table, b: Table) => JSON.stringify(a) === JSON.stringify(b);

// --- Live stream (RtGet) for TR realtime fields ------------------------------

const LIVE_RETRY_MS = 4000;

/** The FIDs (upper case) an RtGet slice's `absent` evidence says its record does not have. */
function absentFids(t: TableResult | null | undefined): Set<string> {
  const out = new Set<string>();
  for (const a of t?.absent ?? []) for (const f of a.fields) out.add(String(f).toUpperCase());
  return out;
}

/** Whether an RtGet slice carries at least one value. */
function hasLiveValue(t: TableResult | null | undefined): boolean {
  return !!t && t.data.length > 0 && t.data[0].some((v) => v !== null && v !== undefined && v !== "");
}

/** A RIC as the hub may echo it: case aside, and `/VOD.L` for a delayed VOD.L. */
const looseRic = (r: string) => ricKey(String(r ?? "")).replace(/^\//, "").toUpperCase();

/**
 * The `unresolved` evidence of `ric`'s RtGet slice: true when the backend
 * snapshot listed it (unknown RIC), false when it sent a list without it
 * (known RIC), null when it sent no list (no evidence).
 */
function liveUnresolved(t: TableResult | null | undefined, ric: string): boolean | null {
  if (!t || !Array.isArray(t.unresolved)) return null;
  return t.unresolved.some((u) => looseRic(u) === looseRic(ric));
}

/**
 * Live-stream fields via RtGet, batched. RtGet waits ~3 s for a first tick;
 * a RIC the hub serves on its delayed feed (`/ACCP.PA`, not entitled to
 * realtime) needs longer than that to subscribe, so its first answer is
 * empty while a second one seconds later has the value. Ask the empty ones
 * once more after LIVE_RETRY_MS, as one batch. A RIC whose answer carried
 * `unresolved` evidence is not re-asked: the backend snapshot already says
 * it is unknown, or known and empty (an unquoted bond), so waiting changes
 * nothing. (The re-ask is the fallback for a backend without that evidence.)
 */
async function fetchLive(rics: string[], liveArg: string): Promise<SettledRics> {
  const spec = {
    maxRics: RT_BATCH,
    build: (batch: string[]) => buildCall("RtGet", [q("IDN"), q(batch.join(",")), q(liveArg)]),
    split: (t: TableResult) => splitByInstrument(t),
    own: dropInstrumentColumn,
  };
  const first = await fetchRicsSettled(`RT|${liveArg}`, rics, spec);
  const cold = rics.filter((r) => {
    const t = first.results.get(ricKey(r));
    return r && !hasLiveValue(t) && liveUnresolved(t, r) === null;
  });
  if (!cold.length) return first;
  await new Promise((r) => setTimeout(r, LIVE_RETRY_MS));
  const second = await fetchRicsSettled(`RT2|${liveArg}`, cold, spec);
  second.results.forEach((t, k) => {
    if (hasLiveValue(t)) {
      first.results.set(k, t);
      first.errors.delete(k);
    }
  });
  return first;
}

// --- TR ------------------------------------------------------------------------

const hasRows = (t: TableResult | null | undefined) => !!t && t.data.length > 0;

/** Result grid for TR / RDP.Data as one plain grid (date cells as bare serials). */
export async function trGrid(instruments: any, fields: any, parameters?: any): Promise<any[][]> {
  return unmarkDates((await trLaid(instruments, fields, parameters)).grid);
}

/** Result table for TR / RDP.Data, for the static writer. */
export async function trTable(instruments: any, fields: any, parameters?: any): Promise<Table> {
  return (await trLive(instruments, fields, parameters)).table;
}

/**
 * TR / RDP.Data for the static writer, with a live feed when it has realtime
 * fields (CF_*, bare FIDs): their ticks merge into the live-path result and
 * the whole table is laid out again, so every rule (0 for a known RIC's empty
 * FID, the record-not-found text of an unknown one, CF_* fallbacks) holds.
 * A RIC the live evidence lists as unknown ignores ticks.
 */
export async function trLive(instruments: any, fields: any, parameters?: any): Promise<Built> {
  const { laid, fetched } = await trFetchAndLay(instruments, fields, parameters);
  let table = toTable(laid.grid, laid.cornerEmpty);
  if (!fetched || !fetched.plan.realtime.length) return { table, live: null };
  const f = fetched;
  const fids = f.plan.realtime;
  const rics = Array.from(
    new Map(f.rics.filter((r) => r && !isInvalidIdentifier(r)).map((r) => [looseRic(r), r] as [string, string])).values()
  );
  return {
    table,
    live: {
      rics,
      fields: fids,
      tick(ric, values) {
        let changed = false;
        // Every typed spelling of the RIC (a range may repeat it) shares the tick.
        for (const r of new Set(f.rics.filter((x) => x && looseRic(x) === looseRic(ric)))) {
          if (mergeTick(f.live, r, fids, values)) changed = true;
        }
        if (!changed) return null;
        const again = layTr(f);
        const next = toTable(again.grid, again.cornerEmpty);
        if (sameTable(next, table)) return null;
        table = next;
        return next;
      },
    },
  };
}

/**
 * Merge a tick's `fids` into `r`'s live-path row (its RtGet slice), creating
 * the row when the first fetch had none. False when nothing changed or the
 * live evidence lists the RIC as unknown.
 */
function mergeTick(live: SettledRics, r: string, fids: string[], values: Record<string, any>): boolean {
  const k = ricKey(r);
  const t = live.results.get(k) || null;
  if (liveUnresolved(t, r) === true) return false;
  const columns = t && t.data.length ? t.columns.slice() : fids.slice();
  const row = t && t.data.length ? t.data[0].slice() : fids.map(() => null);
  let changed = false;
  for (const fid of fids) {
    const v = tickValue(values, fid);
    if (v === undefined) continue;
    let i = columns.findIndex((c) => String(c).toUpperCase() === fid.toUpperCase());
    if (i < 0) {
      columns.push(fid);
      row.push(null);
      i = columns.length - 1;
    }
    if (row[i] !== v) {
      row[i] = v;
      changed = true;
    }
  }
  if (!changed) return false;
  // A FID that ticks is part of the record after all.
  const ticked = new Set(fids.filter((fid) => tickValue(values, fid) !== undefined).map((f) => f.toUpperCase()));
  const absent = t?.absent?.map((a) => ({ ...a, fields: a.fields.filter((f) => !ticked.has(String(f).toUpperCase())) }));
  live.results.set(k, { ...(t || {}), ...(absent ? { absent } : {}), columns, data: [row] });
  live.errors.delete(k);
  return true;
}

async function trLaid(instruments: any, fields: any, parameters?: any): Promise<Laid> {
  return (await trFetchAndLay(instruments, fields, parameters)).laid;
}

/** What a TR result was laid out from: kept so a tick can lay it out again. */
interface TrFetched {
  rics: string[];
  fieldList: string[];
  plan: TrPlan;
  display: TrDisplay;
  grid: SettledRics;
  live: SettledRics;
}

/** Fetch and lay out a TR result; `fetched` is null when nothing was fetched (argument errors, auth). */
async function trFetchAndLay(
  instruments: any,
  fields: any,
  parameters?: any
): Promise<{ laid: Laid; fetched: TrFetched | null }> {
  const fail = (s: string) => ({ laid: plain(text(s)), fetched: null });
  const rics = flattenInstruments(instruments);
  const fieldList = flattenFields(fields);
  if (!rics.some(Boolean)) return fail(LSEG_TEXT.NO_UNIVERSE);
  if (!fieldList.length) return fail(LSEG_TEXT.NO_DATA_ITEM);

  const { params, display, error } = parseTrParams(scalarText(parameters));
  if (error) return fail(error);
  // Invalid identifiers ("-") are not sent: their rows read "The invalid
  // identifier ignored." (layTr), and a call with nothing else shows it alone
  // (golden z_tr_invalid_identifier_single).
  const valid = rics.filter((r) => r && !isInvalidIdentifier(r));
  if (!valid.length) return fail(LSEG_TEXT.INVALID_IDENTIFIER_IGNORED);
  const plan = planTr(fieldList, display);
  const fieldsArg = plan.request.join(";");
  const liveArg = plan.realtime.join(";");
  const none: SettledRics = { results: new Map(), errors: new Map(), error: null };
  let grid: SettledRics;
  let live: SettledRics;
  try {
    // DataGrid for TR.* fields, the live path (RtGet) for every realtime
    // FID (CF_*, BID…); both batched, fetched in parallel, merged per RIC.
    [grid, live] = await Promise.all([
      plan.request.length
        ? fetchRicsSettled(`TR|${fieldsArg}|${params}`, valid, {
            maxRics: TR_BATCH,
            build: (batch) => buildCall("TR", [q(batch.join(",")), q(fieldsArg), q(params)]),
            split: (t) => splitByInstrument(t),
            own: dropInstrumentColumn,
          })
        : Promise.resolve(none),
      plan.realtime.length ? fetchLive(valid, liveArg) : Promise.resolve(none),
    ]);
  } catch (e) {
    // Only auth failures escape fetchRicsSettled: nothing else can succeed.
    return fail(errorToCellText(e, "TR"));
  }
  const fetched: TrFetched = { rics, fieldList, plan, display, grid, live };
  return { laid: layTr(fetched), fetched };
}

/** Lay out a fetched TR result (again after a tick merged into `live`). */
function layTr({ rics, fieldList, plan, display, grid, live }: TrFetched): Laid {
  const key = (r: string) => ricKey(r);
  // A RIC the live evidence lists as unknown may come back with no row at
  // all; give it an all-null one so its realtime cells get the rule below.
  const liveOf = (r: string): TableResult | null => {
    const t = live.results.get(key(r)) || null;
    if (t && !t.data.length && liveUnresolved(t, r) === true)
      return { ...t, columns: plan.realtime, data: [plan.realtime.map(() => null)] };
    return t;
  };
  const byRic = (r: string) => mergeRealtime(grid.results.get(key(r)) || null, liveOf(r));
  // A RIC's data was collected when DataGrid returned rows for it, or the
  // live stream a value. When the plan is realtime-only, a live row counts
  // even if its values are null (present but null stays blank). In a mixed
  // plan it does not: the interface answers an unknown RIC on RtGet with an
  // all-null row, so DataGrid decides there.
  const collected = (r: string) =>
    (plan.request.length > 0 && hasRows(grid.results.get(key(r)))) ||
    (plan.realtime.length > 0 &&
      (plan.request.length > 0 ? hasLiveValue(liveOf(r)) : hasRows(liveOf(r))));
  const errorOf = (r: string) => (collected(r) ? null : grid.errors.get(key(r)) || live.errors.get(key(r)) || null);
  // Invalid identifiers were not asked: they never count as failed RICs.
  const nonBlank = rics.filter((r) => r && !isInvalidIdentifier(r));

  // Every RIC failed outright (always so for a single RIC): show why, as one
  // cell, instead of a grid of row errors. The other rows stay empty (golden
  // tr_range_all_bad: B2 holds the text, B3 is an empty cell, which a 1x1
  // result leaves truly empty where a spilled "" would not).
  const firstError = nonBlank.map(errorOf).find((e) => e);
  if (firstError && nonBlank.every((r) => errorOf(r))) return plain(text(errorToCellText(firstError, "TR")));

  // Per-cell LSEG errors (the hub's `errors` / `unresolved` on each RIC's
  // DataGrid slice, batch5 goldens). A cell with an error entry for its
  // field, or any cell of an unresolved RIC, shows LSEG's row-field text,
  // naming the field as typed; a null cell without one shows "NULL".
  const slice = (r: string) => grid.results.get(key(r)) || null;
  const isUnresolved = (r: string) => !!slice(r)?.unresolved?.length;
  // The hub reports `field` as sent, maybe with a "(Period=FY10)" suffix, or
  // as the header name: compare case-insensitively, suffix stripped, against
  // the typed field, its sources and the response headers it was read from.
  const fieldKey = (f: string) => String(f).replace(/\s*\(.*\)\s*$/, "").trim().toUpperCase();
  const cellError = (r: string, o: TrOutput, headers: string[] = []): string | null => {
    const t = slice(r);
    if (!t) return null;
    const names = [typedField(o), ...o.sources, ...headers].map(fieldKey);
    const hit = (t.errors || []).some((e) => names.includes(fieldKey(e.field)));
    return hit || isUnresolved(r) ? rowFieldError(typedField(o)) : null;
  };
  // "NULL" only on evidence: the hub sent an `errors` list (even []) for this
  // RIC, so a null without an entry is a genuine LSEG null. An older hub, or
  // one whose extra raw call failed, sends none: keep 0.1.14.0's blank.
  // Realtime fields (every source an RT: FID) follow realtime semantics on
  // the live evidence (golden batch6): an empty cell of a RIC the snapshot
  // lists in `unresolved` shows LSEG's record-not-found text, of one it does
  // not list the number 0. No list: keep the blank. CF_LAST, with its
  // TR.PriceClose fallback, is a DataGrid output and keeps its own rule.
  const realtimeCell = (r: string): string | number | null => {
    const u = liveUnresolved(live.results.get(key(r)), r);
    return u === null ? null : u ? LSEG_TEXT.RT_RECORD_NOT_FOUND_TR : 0;
  };
  // A realtime FID the instrument's record does not have at all (the live
  // result's `absent` evidence), as opposed to present but blank (goldens
  // y_tr_cf_last_btc / _eur: TRDPRC_1 of BTC= and EUR= is #N/A, CF_LAST the BID).
  const absent = (r: string, fid: string) => absentFids(live.results.get(key(r))).has(fid.toUpperCase());
  const cells = {
    error: cellError,
    nullText: (r: string) => (Array.isArray(slice(r)?.errors) ? LSEG_TEXT.NULL : null),
    realtime: realtimeCell,
    absent,
  };
  const gridOutputs = plan.outputs.filter(isDataGridOutput);

  // Every RIC unresolved, or a single RIC whose every DataGrid cell has an
  // error (golden tr_valid_ric_empty_field): one UNRESOLVED cell, as for the
  // 422 (golden tr_range_all_bad).
  if (gridOutputs.length && nonBlank.every(isUnresolved)) return plain(text(LSEG_TEXT.UNRESOLVED));
  // The single-RIC rule also needs every DataGrid cell empty: an error entry
  // next to real values (another period, another field) is not UNRESOLVED.
  const only = rics.length === 1 ? slice(rics[0]) : null;
  if (
    only &&
    gridOutputs.length &&
    hasRows(only) &&
    only.data.every((row) => row.every((v) => v === null || v === undefined || v === "")) &&
    gridOutputs.every((o) => cellError(rics[0], o, trOutputHeaders(only.columns, plan, o)))
  )
    return plain(text(LSEG_TEXT.UNRESOLVED));

  // Multi-RIC (a range or a "A;B;C" string alike): a RIC whose fetch failed,
  // or that is absent from a successful response, shows LSEG's row text in
  // every field column, each naming its own field as typed (goldens
  // tr_multi_ric_range B4, tr_range_bad_ric_two_fields B3:C3); one that came
  // back with null values stays blank. A single RIC with no rows keeps the
  // blank grid it had before.
  // A row that failed for a non-LSEG reason (5xx / 429 / network after the
  // batcher's retries) shows the connection text in its first column, not
  // LSEG's business text.
  const rowError =
    rics.length > 1
      ? (r: string) => {
          if (isInvalidIdentifier(r)) return plan.outputs.map(() => LSEG_TEXT.INVALID_IDENTIFIER_IGNORED);
          if (collected(r)) return null;
          const err = errorOf(r);
          if (err && !isLsegError(err)) return [errorToCellText(err, "TR")];
          // A realtime column of a RIC the live evidence lists as unknown
          // shows the realtime text, not the TR.* field text.
          const unknownLive = liveUnresolved(live.results.get(key(r)), r) === true;
          return plan.outputs.map((o, i) =>
            unknownLive && !isDataGridOutput(o) ? LSEG_TEXT.RT_RECORD_NOT_FOUND_TR : rowFieldError(fieldList[i])
          );
        }
      : undefined;
  const laidGrid = layoutTrCells(rics, byRic, plan, display, rowError, cells);
  // The formula cell shows `Updated at` when the table's top-left cell is
  // empty: the corner of row and column headers, or (no CH) the first row's
  // row-header cell, e.g. the date of an invalid identifier's row (golden
  // z_tr_invalid_identifier_rh_ts E2).
  const headed = display.rowHeaders.length > 0 && !display.excludeValues;
  const topEmpty = headed && !display.columnHeaders && !display.transpose && isBlank(unmarkDates(laidGrid)[0]?.[0]);
  return { grid: laidGrid, cornerEmpty: (display.columnHeaders && headed) || topEmpty };
}

// --- RHistory ----------------------------------------------------------------

/** LSEG's RHistory columns when no field is given (golden a_rh_empty_fields). */
const DEFAULT_HISTORY_FIELDS = [".Timestamp", ".High", ".Close", ".Low", ".Open", ".Count", ".Volume"];

const fieldsOf = (fields: any): string[] => (fields === null || fields === undefined ? [] : flattenFields(fields));

/**
 * Result grid for RHistory / RDP.HistoricalPricing in the spill form, either
 * form of arguments: the LSEG one (instruments, fields, request, refresh,
 * display, dest) when the request, the display or the fields look like it,
 * else the EAV positional one (instruments, fields, interval, start, end, count).
 */
export async function rhistoryGrid(
  instruments: any,
  fields?: any,
  request?: any,
  startOrRefresh?: any,
  endOrDisplay?: any,
  countOrDestination?: any
): Promise<any[][]> {
  const refinitiv =
    looksLikeParams(scalarText(request)) || looksLikeParams(scalarText(endOrDisplay)) || hasLegacyHistoryFields(fieldsOf(fields));
  if (!refinitiv) return historyGrid(instruments, fields, request, startOrRefresh, endOrDisplay, countOrDestination);
  return unmarkDates(await rhistoryLaid(instruments, fields, request, startOrRefresh, endOrDisplay));
}

/** Result table for RHistory / RDP.HistoricalPricing (LSEG arguments), for the static writer. */
export async function rhistoryTable(instruments: any, fields?: any, request?: any, refresh?: any, display?: any): Promise<Table> {
  return toTable(await rhistoryLaid(instruments, fields, request, refresh, display));
}

/**
 * Result grid for EAV.HISTORY, the EAV-only positional form
 * (instruments, fields, interval, start, end, count), sent to the backend as is.
 */
export async function historyGrid(
  instruments: any,
  fields?: any,
  interval?: any,
  start?: any,
  end?: any,
  count?: any
): Promise<any[][]> {
  const nonBlank = flattenInstruments(instruments).filter(Boolean);
  if (!nonBlank.length) return text(LSEG_TEXT.NO_UNIVERSE);
  const max = scalarText(count);
  return runAs(
    buildCall("RHistory", [
      q(nonBlank.join(",")),
      q(fieldsOf(fields).join(";")),
      q(scalarText(interval)),
      q(normalizeDate(start)),
      q(normalizeDate(end)),
      n(max === "" ? null : Number(max)),
    ]),
    (e) => errorToCellText(e, "RHISTORY", nonBlank)
  );
}

/** RHistory with LSEG's arguments; date cells still marked. */
async function rhistoryLaid(instruments: any, fields?: any, request?: any, refresh?: any, displayArg?: any): Promise<any[][]> {
  const rics = flattenInstruments(instruments);
  const typed = fieldsOf(fields);
  // No fields: LSEG's default columns, in its order (golden a_rh_empty_fields).
  const fieldList = typed.length ? typed : DEFAULT_HISTORY_FIELDS;
  if (!rics.some(Boolean)) return text(LSEG_TEXT.NO_UNIVERSE);
  const nonBlank = rics.filter(Boolean);
  const reqText = scalarText(request);

  const display = parseHistoryDisplay(scalarText(displayArg));
  if (display.error) return text(display.error);
  // One "today" (the PC's calendar date) for the 0D checks and the window.
  const today = new Date();
  const requestError = historyRequestError(reqText, today);
  if (requestError) return text(requestError);
  // The Refresh argument is checked once the request passes: a relative
  // START is reported whatever it holds (goldens rh_refresh_empty,
  // rh_refresh_frq); before a bad Refresh too [A].
  const refreshError = historyRefreshError(scalarText(refresh));
  if (refreshError) return text(refreshError);

  const tick = isTickInterval(reqText);
  const intraday = tick || isIntradayInterval(reqText);
  let columns: HistoryColumn[];
  try {
    columns = fieldList.map((f) => mapHistoryField(f, intraday));
  } catch {
    // BID./ASK. prefixes (golden rh_bid_ask_prefix) and any other
    // `<FID>.<property>` combination with no known FID.
    return text(LSEG_TEXT.INVALID_FIELDS);
  }
  // Tick data has no bars: .Open/.High/.Low/.Close are invalid fields there
  // (goldens a_rh_intraday_tick, v_rh_interval_tas, w_rh_interval_taq).
  if (tick && fieldList.some(isBarField)) return text(LSEG_TEXT.INVALID_FIELDS);
  if (intraday) {
    // Ticks and intraday bars carry their time of day (m/d/yyyy h:mm) and show
    // the hub's UTC instants on the PC's clock (goldens w_rh_tick_value_nbrows:
    // the FX close 20:59Z reads 22:59 on a Paris PC; x_rh_intraday_aapl_tz,
    // x_rh_interval_*). A bar is labelled by its end, one interval after the
    // hub's start label (spec §6.2); a tick keeps its own instant.
    const shiftMinutes = intradayMinutes(reqText);
    columns = columns.map((c) =>
      c.kind === "timestamp" ? { ...c, datetime: true, localTime: true, ...(shiftMinutes ? { shiftMinutes } : {}) } : c
    );
  }
  const win = widenHistoryWindow(normalizeHistoryRequest(reqText), today);
  const hasData = (t: TableResult | null | undefined) => !!t && trimHistory(t, win.from, win.to).data.length > 0;
  /** Rows with a value in one of `fids` (no FID given: any row). */
  const hasValues = (t: TableResult | null | undefined, fids: string[]) => {
    if (!t || !hasData(t)) return false;
    if (!fids.length) return true;
    const idx = fids.map((f) => t.columns.findIndex((c) => String(c).toUpperCase() === f.toUpperCase())).filter((i) => i >= 0);
    return trimHistory(t, win.from, win.to).data.some((row) => idx.some((i) => !isBlank(row[i])));
  };

  // Fallback columns (.Close / .Value: OFF_CLOSE and TRDPRC_1, first non-empty
  // wins per row) never ask both FIDs in one request: a request mixing a RIC
  // that has both with RICs that have one fails upstream ("Names should be
  // list-like for a MultiIndex"), and a RIC without one of them (.FCHI has no
  // OFF_CLOSE, SAPG.DE no TRDPRC_1) rejects the whole request. So:
  // - A: every single-FID column plus the first fallback FID (interday
  //   OFF_CLOSE, intraday TRDPRC_1);
  // - A' (only for RICs that rejected A): the single-FID columns alone;
  // - B: the other fallback FID alone. Interday it is always asked, beside A,
  //   and merged row by row on the timestamp, so a day with a null OFF_CLOSE
  //   (today during the session) shows that day's TRDPRC_1 (spec §6.2).
  //   Intraday only for RICs whose A has no fallback value (as before).
  // - C (interday, GC4): a RIC whose universe rejects both OFF_CLOSE and
  //   TRDPRC_1 (90006, e.g. the EURIBOR1YD= fixing) is asked with the hub's
  //   default fields, alone; its value column (defaultValueColumn: FIXING_1)
  //   fills the fallback columns, merged per day like B. Its request uses the
  //   same widened window, so the START day is kept (golden z_rh_euribor1yd).
  const fidUp = (f: string) => f.toUpperCase();
  const fallbackCols = columns.filter((c): c is Extract<HistoryColumn, { kind: "field" }> => c.kind === "field" && c.fids.length > 1);
  const first = fallbackCols.length ? fallbackCols[0].fids[0] : null;
  const fallbackFids = ([] as string[]).concat(...fallbackCols.map((c) => c.fids));
  const fallbackSet = new Set(fallbackFids.map(fidUp));
  const uniq = (fs: string[]) => fs.filter((f, i) => fs.findIndex((x) => fidUp(x) === fidUp(f)) === i);
  const fidsA = uniq(
    columns
      .filter((c): c is Extract<HistoryColumn, { kind: "field" }> => c.kind === "field")
      .map((c) => (c.fids.length > 1 && first && c.fids.some((f) => fidUp(f) === fidUp(first)) ? first : c.fids[0]))
  );
  const fidsN = fidsA.filter((f) => !fallbackSet.has(fidUp(f)));
  const fidsB = uniq(fallbackFids).filter((f) => !fidsA.some((x) => fidUp(x) === fidUp(f)));
  /** Fetch `fids` for `rics`: batched, or (alone) one request per RIC under its own key. */
  const fetchFids = (fids: string[], rics: string[], alone = false) =>
    fetchRicsSettled(`RH${alone ? "1" : ""}|${fids.join(";")}|${win.request}`, rics, {
      maxRics: alone ? 1 : HISTORY_BATCH,
      build: (batch) => buildCall("RHistory", [q(batch.join(",")), q(fids.join(";")), q(win.request)]),
      split: (t, batch) => splitHistory(t, batch, fids),
      own: (t) => t,
      // No row and no `unresolved` evidence: says nothing about the RIC.
      isEmpty: (t) => t.data.length === 0 && !(t.unresolved && t.unresolved.length),
      ...(alone ? { lane: "solo" as const } : {}),
    });
  const none: SettledRics = { results: new Map(), errors: new Map(), error: null };

  /** One RIC's tier answers: A (or A'), B, and the errors of each. */
  interface Tiers {
    a: TableResult | null;
    b: TableResult | null;
    errA: any;
    errA2: any;
    errB: any;
  }
  /** Run tiers A / A' / B for `rics` (see above); `alone`: one request per RIC. */
  const runTiers = async (rics: string[], alone: boolean): Promise<Map<string, Tiers>> => {
    const interdayB = !intraday && fidsB.length > 0;
    const [gotA, gotB0] = await Promise.all([
      fetchFids(fidsA, rics, alone),
      interdayB ? fetchFids(fidsB, rics, alone) : Promise.resolve(none),
    ]);
    const rejectedA = rics.filter((r) => gotA.errors.has(ricKey(r)) && !hasData(gotA.results.get(ricKey(r))));
    const gotA2 = rejectedA.length && fidsN.length && fidsN.length < fidsA.length ? await fetchFids(fidsN, rejectedA, alone) : none;
    const sliceA = (k: string) => {
      const a = gotA.results.get(k);
      return hasData(a) ? a! : gotA2.results.get(k) ?? a ?? null;
    };
    const needB = !intraday || !fidsB.length ? [] : rics.filter((r) => !hasValues(sliceA(ricKey(r)), first ? [first] : []));
    const gotB = interdayB ? gotB0 : needB.length ? await fetchFids(fidsB, needB, alone) : none;
    const out = new Map<string, Tiers>();
    for (const r of rics) {
      const k = ricKey(r);
      out.set(k, { a: sliceA(k), b: gotB.results.get(k) ?? null, errA: gotA.errors.get(k), errA2: gotA2.errors.get(k), errB: gotB.errors.get(k) });
    }
    return out;
  };

  const results = new Map<string, TableResult | null>();
  const errors = new Map<string, any>();
  // RICs a successful response listed in `unresolved` (and with no data anywhere).
  const unresolved = new Set<string>();
  try {
    const tiers = await runTiers(nonBlank, false);
    // GC5: a RIC left with nothing (no row, no error, no evidence) whose
    // slice came empty from a multi-RIC answer where other RICs had rows is
    // asked again alone: lseg.data swallows a per-RIC failure inside a
    // multi-RIC get_history (Calcul perf reuters run: .MXTDSB50, .SOUSAI50,
    // .SGOD6 gave #VALUE! in the batch, values alone).
    // The re-ask is shared by every cell asking the same RIC, window and
    // FIDs, delayed, and budgeted (reask.ts); over budget the RIC keeps its
    // no-data result.
    const nothing = (t: Tiers) => !hasData(t.a) && !hasData(t.b) && t.errA === undefined && t.errA2 === undefined && t.errB === undefined;
    const seen = new Set<string>();
    const swallowed = nonBlank.filter((r) => {
      const k = ricKey(r);
      if (seen.has(k)) return false;
      seen.add(k);
      const t = tiers.get(k)!;
      return nothing(t) && (!!t.a?.emptyInBatch || !!t.b?.emptyInBatch);
    });
    const soloKey = (r: string) => [fidsA.join(";"), fidsB.join(";"), fidsN.join(";"), intraday, win.request, ricKey(r)].join("|");
    await Promise.all(
      swallowed.map(async (r) => {
        const k = ricKey(r);
        const solo = await sharedReask(soloKey(r), async () => (await runTiers([r], true)).get(k)!);
        if (!solo) return;
        // Still throttled (429, 5xx, network) on its own: the RIC keeps the
        // no-data outcome it had before the re-ask, never "Connection was
        // lost", and a 90006 beside a 429 is not reported either.
        const errs = [solo.errA, solo.errA2, solo.errB].filter((e) => e !== undefined);
        tiers.set(k, errs.some(isTransient) ? { ...solo, errA: undefined, errA2: undefined, errB: undefined } : solo);
      })
    );
    // RICs whose universe has neither fallback FID: tier C.
    const noFallbackFid: string[] = [];
    for (const r of nonBlank) {
      const k = ricKey(r);
      const { a, b, errA, errA2, errB } = tiers.get(k)!;
      const merged = mergeHistory(hasData(a) ? a : null, hasData(b) ? b : null);
      if (
        !intraday &&
        fallbackCols.length > 0 &&
        isUnsupportedFieldsError(errA) &&
        isUnsupportedFieldsError(errB) &&
        !hasValues(merged, fallbackFids) &&
        !noFallbackFid.some((x) => ricKey(x) === k)
      ) {
        noFallbackFid.push(r);
      }
      if (merged) {
        results.set(k, merged);
        continue;
      }
      const listed = [a, b].some((t) => !!t && !!t.unresolved && t.unresolved.length > 0);
      // The fallback FID refused after a clean, empty first tier (intraday
      // before the day's first bar: TRDPRC_1 empty, OFF_CLOSE "does not
      // support the following fields") is no reason: the RIC has no rows,
      // and LSEG shows #N/A (golden rh_intraday_60m_0d_td). Interday too [A].
      const cleanA = errA === undefined && errA2 === undefined;
      const err = errA2 ?? errA ?? (cleanA && isUnsupportedFieldsError(errB) ? undefined : errB);
      if (listed) unresolved.add(k);
      else if (err) errors.set(k, err);
    }
    if (noFallbackFid.length && first) {
      // One request per RIC (the hub's default fields differ by instrument),
      // shared by every cell asking it at once, in the solo lane.
      const gotC = new Map<string, TableResult | null>();
      await Promise.all(
        noFallbackFid.map(async (r) => {
          const got = await shareInFlight(`C|${win.request}|${ricKey(r)}`, () =>
            fetchRicsSettled(`RH|*|${win.request}`, [r], {
              maxRics: 1,
              build: (batch) => buildCall("RHistory", [q(batch.join(",")), null, q(win.request)]),
              split: (t, batch) => new Map([[ricKey(batch[0]), t]]),
              own: (t) => t,
              lane: "solo",
            })
          );
          gotC.set(ricKey(r), got.results.get(ricKey(r)) ?? null);
        })
      );
      for (const r of noFallbackFid) {
        const k = ricKey(r);
        const c = gotC.get(k);
        const j = c ? defaultValueColumn(c) : -1;
        if (!c || j < 0 || !hasData(c)) continue;
        const value: TableResult = { columns: [first], data: c.data.map((row) => [row[j]]), index: c.index };
        results.set(k, mergeHistory(results.get(k) ?? null, value)!);
        errors.delete(k);
        unresolved.delete(k);
      }
    }
  } catch (e) {
    // Only auth failures escape fetchRicsSettled.
    return text(errorToCellText(e, "RHISTORY", nonBlank));
  }

  const byRic = (r: string) => {
    const t = results.get(ricKey(r));
    return t ? trimHistory(t, win.from, win.to) : null;
  };
  // Multi-RIC with some data: a RIC that failed shows its reason in its own
  // column, "Invalid RIC." for an unresolved one (golden rh_one_good_one_bad):
  // listed in a response's `unresolved`, or failed with a not-found 422. An
  // all-null column with neither stays blank (a valid RIC with no data).
  const failedText = (r: string) => {
    if (results.has(ricKey(r))) return null;
    if (unresolved.has(ricKey(r))) return LSEG_TEXT.INVALID_RIC;
    const e = errors.get(ricKey(r));
    if (!e) return null;
    return isUnresolvedError(e) ? LSEG_TEXT.INVALID_RIC : errorToCellText(e, "RHISTORY");
  };
  const grid = layoutHistoryCells(rics, byRic, columns, display, failedText);
  if (grid.length) return grid;
  if (errors.size || unresolved.size) {
    // No RIC has data: one cell, listing every failed RIC
    // ("Invalid RIC(s): NOTARIC.XX, ALSOBAD.YY", golden rh_two_bad_rics).
    const failed = Array.from(new Set(nonBlank.filter((r) => errors.has(ricKey(r)) || unresolved.has(ricKey(r)))));
    return text(errors.size ? errorToCellText(errors.values().next().value, "RHISTORY", failed) : invalidRics(failed));
  }
  // No rows at all (a weekend's START:0D END:0D, or hub: 200, columns [] /
  // data []): the usual table with one row of #N/A, the formula cell's being
  // the text (goldens x_rh_start_0d*, rh_valid_ric_no_rows).
  return layoutNoRows(rics, columns, display);
}

// --- RtGet ---------------------------------------------------------------------

/**
 * The Source sent to the backend: as typed, or IDN when left empty, which
 * LSEG reads as its default feed (golden o_rtget_no_source). The interface
 * needs it, or it reads the instrument as the source.
 */
const rtSource = (source: any): string => scalarText(source) || "IDN";

/**
 * Result grid for RtGet / RDP.Price. Arguments are positional
 * (Source, Instrument, Field, Request), as in LSEG: `RDP.Price("VOD.L","BID")`
 * reads VOD.L as the source and has no field (golden rdpprice_vod_bid).
 */
export async function rtgetGrid(source: any, instruments?: any, fields?: any): Promise<any[][]> {
  const fieldText = scalarText(fields);
  if (!fieldText) return text(LSEG_TEXT.RT_NO_FIELD);
  const ricText = scalarText(instruments);
  const formula = buildCall("RtGet", [q(rtSource(source)), q(ricText), q(fieldText)]);
  const toText = (e: any) => errorToCellText(e, "RTGET");
  const list = (s: string) => s.split(/[,;]/).map((x) => x.trim()).filter(Boolean);
  const ricList = list(ricText);
  // Several instruments or fields keep the Instrument-headed grid: no golden
  // pins LSEG's layout for those yet.
  if (ricList.length !== 1 || list(fieldText).length !== 1) return runAs(formula, toText);

  return (await rtgetOne(formula, ricList[0])).grid;
}

/**
 * One instrument, one field: LSEG shows the bare value (golden
 * rtget_vod_bid). `unknown`: the backend listed the RIC as not found.
 */
async function rtgetOne(formula: string, ric: string): Promise<{ grid: any[][]; unknown: boolean }> {
  let t: TableResult;
  try {
    t = (await evaluate(formula)).result;
  } catch (e) {
    return { grid: text(errorToCellText(e, "RTGET")), unknown: false };
  }
  // The RIC listed in `unresolved` is not found (golden rtget_invalid_ric).
  // A null value is 0 only when the hub sent an `unresolved` list (even [])
  // without the RIC: a valid RIC, as LSEG shows a bond's empty TRDPRC_1
  // (golden rtget_valid_empty_fid). With no list, unknown is the likelier
  // case, so a null stays "not found" (0.1.14.0's intent).
  if (liveUnresolved(t, ric) === true) return { grid: text(LSEG_TEXT.RT_NOT_FOUND), unknown: true };
  const col = t.columns.findIndex((c) => String(c).toUpperCase() !== "INSTRUMENT");
  const v = col >= 0 && t.data.length ? t.data[0][col] : null;
  if (!isBlank(v)) return { grid: [[v]], unknown: false };
  return { grid: Array.isArray(t.unresolved) ? [[0]] : text(LSEG_TEXT.RT_NOT_FOUND), unknown: false };
}

/**
 * Result table for RtGet / RDP.Price, for the static writer. LSEG reads the
 * Instrument as one RIC: a range gives its first cell (golden
 * a_rtget_multi_ric_range), and a comma list is a single RIC no record has
 * (golden a_rtget_multi_ric). The multi-RIC grid is EAV.RTGETS (rtgetGrid).
 */
export async function rtgetTable(source: any, instrument?: any, fields?: any): Promise<Table> {
  return (await rtgetLive(source, instrument, fields)).table;
}

/**
 * RtGet / RDP.Price for the static writer, with a live feed for the one
 * instrument, one field form (the layout LSEG's goldens pin): each tick of
 * the field replaces the value. A RIC the backend listed as unknown keeps
 * LSEG's not-found text whatever the stream says.
 */
export async function rtgetLive(source: any, instrument?: any, fields?: any): Promise<Built> {
  const ric = scalarText(instrument);
  // A field range reads its first cell (golden o_rtget_field_range); a field
  // list ("BID;ASK") is one field no record has (golden o_rtget_two_fields).
  const field = scalarText(fields);
  if (/[,;]/.test(field)) return { table: toTable(text(LSEG_TEXT.RT_INVALID_FIELD)), live: null };
  if (field && /[,;]/.test(ric)) return { table: toTable(text(LSEG_TEXT.RT_NOT_FOUND)), live: null };
  if (!field || !ric) return { table: toTable(await rtgetGrid(source, ric, fields)), live: null };
  const one = await rtgetOne(buildCall("RtGet", [q(rtSource(source)), q(ric), q(field)]), ric);
  let table = toTable(one.grid);
  return {
    table,
    live: {
      rics: [ric],
      fields: [field],
      tick(_ric, values) {
        const v = tickValue(values, field);
        if (one.unknown || v === undefined) return null;
        const next = toTable([[v]]);
        if (sameTable(next, table)) return null;
        table = next;
        return next;
      },
    },
  };
}

/** Evaluate a formula as-is; a failure becomes the single text cell `toText` gives it. */
async function runAs(formula: string, toText: (e: any) => string): Promise<any[][]> {
  try {
    return toMatrix(await evaluate(formula));
  } catch (e) {
    return text(toText(e));
  }
}
